/**
 * PSC Widget Context Provider
 *
 * Provides configuration and state management for the PSC widget.
 */

import React, { createContext, useMemo, useState, useCallback } from 'react';
import type {
  PSCWidgetConfig,
  ResolvedConfig,
  CapturedContext,
  RequirementType,
} from './types';
import { DEFAULT_CONFIG } from './types';
import { useContextCapture } from './hooks/useContextCapture';
import { useSessionRecording } from './hooks/useSessionRecording';
import type { SessionRecording } from './hooks/useSessionRecording';
import { createPSCApiClient, createMockPSCApiClient, PSCApiError } from './services/pscApi';
import { sanitizeContext } from './services/contextSanitizer';
import type { PSCApiClient } from './services/pscApi';
import type { Screenshot } from './hooks/useScreenshotCapture';

export interface DraftFormData {
  title: string;
  description: string;
  priority: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  category?: string;
}

export interface PSCContextValue {
  /** Resolved configuration */
  config: ResolvedConfig;

  /** Whether the dialog is open */
  isOpen: boolean;

  /** Open the dialog */
  open: () => void;

  /** Close the dialog */
  close: () => void;

  /** Current captured context */
  context: CapturedContext;

  /** Refresh context capture */
  refreshContext: () => void;

  /** Clear captured errors */
  clearErrors: () => void;

  /** API client instance */
  api: PSCApiClient;

  /** Current step in the submission flow */
  currentStep: number;

  /** Set current step */
  setCurrentStep: (step: number) => void;

  /** Selected requirement type */
  requirementType: RequirementType | null;

  /** Set requirement type */
  setRequirementType: (type: RequirementType | null) => void;

  /** Submit requirement */
  submitRequirement: (data: {
    type: RequirementType;
    title: string;
    description: string;
    priority: string;
    additionalData?: Record<string, unknown>;
    screenshots?: Array<{
      dataUrl: string;
      captureMode: string;
      width: number;
      height: number;
    }>;
    includeRecording?: boolean;
  }) => Promise<string>;

  /** Whether session recording is active */
  isRecording: boolean;

  /** Export current session recording */
  exportRecording: () => SessionRecording | null;

  /** Loading state */
  isSubmitting: boolean;

  /** Error state */
  error: string | null;

  /** Clear error */
  clearError: () => void;

  /** Draft form data persisted across dialog open/close */
  draftFormData: DraftFormData | null;

  /** Update draft form data */
  setDraftFormData: (data: DraftFormData | null) => void;

  /** Draft screenshots persisted across dialog open/close */
  draftScreenshots: Screenshot[];

  /** Update draft screenshots */
  setDraftScreenshots: (screenshots: Screenshot[]) => void;

  /** Submitted requirement ID (persisted for success screen) */
  submittedId: string | null;

  /** Set submitted requirement ID */
  setSubmittedId: (id: string | null) => void;

  /** Reset all draft state (call after successful submission is acknowledged) */
  resetDraft: () => void;
}

export const PSCContext = createContext<PSCContextValue | null>(null);

interface PSCProviderProps {
  config: PSCWidgetConfig;
  children: React.ReactNode;
  useMockApi?: boolean;
}

export function PSCProvider({
  config: userConfig,
  children,
  useMockApi = false,
}: PSCProviderProps) {
  // Resolve configuration with defaults
  const config = useMemo<ResolvedConfig>(() => {
    return {
      ...DEFAULT_CONFIG,
      ...userConfig,
      redactPatterns: userConfig.redactPatterns || [],
    };
  }, [userConfig]);

  // Dialog state
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [requirementType, setRequirementType] = useState<RequirementType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft state — persisted across dialog open/close cycles
  const [draftFormData, setDraftFormData] = useState<DraftFormData | null>(null);
  const [draftScreenshots, setDraftScreenshots] = useState<Screenshot[]>([]);
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  // Context capture
  const {
    context,
    refreshContext,
    clearErrors,
    // addEntity, removeEntity available for future use
  } = useContextCapture({
    appName: config.appName,
    captureConsoleErrors: config.captureConsoleErrors,
    captureNetworkErrors: config.captureNetworkErrors,
    captureEntities: config.captureEntities,
    maxConsoleErrors: config.maxConsoleErrors,
    maxNetworkErrors: config.maxNetworkErrors,
    redactPatterns: config.redactPatterns,
    getUserInfo: config.getUserInfo,
    getSelectedEntities: config.getSelectedEntities,
  });

  // Session recording
  const {
    isRecording,
    exportRecording,
  } = useSessionRecording({
    enabled: config.captureSessionRecording ?? true,
    bufferDuration: config.recordingBufferDuration ?? 30,
  });

  // API client
  const api = useMemo<PSCApiClient>(() => {
    if (useMockApi) {
      return createMockPSCApiClient();
    }
    return createPSCApiClient({
      baseUrl: config.apiBaseUrl,
      getAuthToken: config.getAuthToken,
    });
  }, [config.apiBaseUrl, config.getAuthToken, useMockApi]);

  // Open dialog — preserves draft state so form data survives open/close cycles
  const open = useCallback(() => {
    if (!config.enabled) return;
    refreshContext();
    setIsOpen(true);
  }, [config.enabled, refreshContext]);

  // Close dialog — preserves draft state so form data survives open/close cycles
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Reset all draft state — call after user acknowledges successful submission
  const resetDraft = useCallback(() => {
    setCurrentStep(0);
    setRequirementType(null);
    setDraftFormData(null);
    setDraftScreenshots([]);
    setSubmittedId(null);
    setError(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Submit requirement
  const submitRequirement = useCallback(
    async (data: {
      type: RequirementType;
      title: string;
      description: string;
      priority: string;
      additionalData?: Record<string, unknown>;
      screenshots?: Array<{
        dataUrl: string;
        captureMode: string;
        width: number;
        height: number;
      }>;
      includeRecording?: boolean;
    }): Promise<string> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Sanitize context before sending
        const sanitizedContext = sanitizeContext(context, config.redactPatterns);

        // Create requirement
        const requirement = await api.createRequirement({
          requirement_type: data.type,
          source_service_id: config.appName.toLowerCase().replace(/\s+/g, '-'),
          page_context: sanitizedContext,
          title: data.title,
          description: data.description,
          priority: data.priority as 'low' | 'medium' | 'high' | 'critical',
        });

        // Save additional data if provided
        if (data.additionalData) {
          await api.saveQuestionnaireData(
            requirement.requirement_id,
            {
              title: data.title,
              description: data.description,
              priority: data.priority,
              ...data.additionalData,
            },
            false
          );
        }

        // Upload screenshots if provided
        if (data.screenshots && data.screenshots.length > 0) {
          await api.uploadScreenshots?.(
            requirement.requirement_id,
            data.screenshots.map((s) => ({
              image_data: s.dataUrl,
              metadata: {
                capture_type: s.captureMode,
                viewport_size: { width: s.width, height: s.height },
              },
            }))
          );
        }

        // Upload session recording if requested
        if (data.includeRecording) {
          const recording = exportRecording();
          if (recording) {
            // Convert Uint8Array to base64 (chunked to avoid call stack overflow)
            let binary = '';
            const bytes = recording.data;
            const chunkSize = 8192;
            for (let i = 0; i < bytes.length; i += chunkSize) {
              binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
            }
            const base64Data = btoa(binary);
            await api.uploadRecording?.(requirement.requirement_id, {
              recording_data: base64Data,
              metadata: {
                duration: recording.duration,
                event_count: recording.eventCount,
                start_time: recording.startTime,
                end_time: recording.endTime,
                original_size: recording.originalSize,
                compressed_size: recording.compressedSize,
              },
            });
          }
        }

        // Submit for review
        await api.submitForReview(requirement.requirement_id);

        // Call success callback
        config.onSubmitSuccess?.(requirement.requirement_id);

        // Clear errors after successful submission
        clearErrors();

        return requirement.requirement_id;
      } catch (err) {
        const errorMessage =
          err instanceof PSCApiError
            ? err.message
            : err instanceof Error
            ? err.message
            : 'Failed to submit requirement';

        setError(errorMessage);
        config.onSubmitError?.(new Error(errorMessage));
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [api, context, config, clearErrors, exportRecording]
  );

  const value = useMemo<PSCContextValue>(
    () => ({
      config,
      isOpen,
      open,
      close,
      context,
      refreshContext,
      clearErrors,
      api,
      currentStep,
      setCurrentStep,
      requirementType,
      setRequirementType,
      submitRequirement,
      isSubmitting,
      error,
      clearError,
      isRecording,
      exportRecording,
      draftFormData,
      setDraftFormData,
      draftScreenshots,
      setDraftScreenshots,
      submittedId,
      setSubmittedId,
      resetDraft,
    }),
    [
      config,
      isOpen,
      open,
      close,
      context,
      refreshContext,
      clearErrors,
      api,
      currentStep,
      requirementType,
      submitRequirement,
      isSubmitting,
      error,
      clearError,
      isRecording,
      exportRecording,
      draftFormData,
      draftScreenshots,
      submittedId,
      resetDraft,
    ]
  );

  return <PSCContext.Provider value={value}>{children}</PSCContext.Provider>;
}
